// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class CommunicationsService {
  createdCommunication$ = new BehaviorSubject<boolean>(false);

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) { }

  /**
   * Create a new communication
   *
   * @since 1.0.0
   *
   * @param communication Communication data
   * @returns Observable<any>
   */
  create(communication: any): Observable<any> {
    if (communication.companyData) {
      if (communication.companyData.documents) {
        delete communication.companyData.documents;
      }
      if (communication.companyData.phonebook) {
        delete communication.companyData.phonebook;
      }
      if (communication.companyData.subProperties) {
        delete communication.companyData.subProperties;
      }
      if (communication.companyData.subUsers) {
        delete communication.companyData.subUsers;
      }
    }
    return this.apiService.apiCall('POST', 'communications', communication);
  }

  /**
   * Create a new communication
   *
   * @since 1.0.0
   *
   * @param communication Communication data
   * @returns Observable<any>
   */
  createSuppliers(communication: any): Observable<any> {
    return this.apiService.apiCall('POST', 'communications/suppliers', communication);
  }

  /**
   * Get communications list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'communications', params);
  }

  /**
   * Get an exportation file of all communications
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'communications/export', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given communicationId data from back-end
   *
   * @since 1.0.0
   *
   * @param communicationId ID of the communication to get
   */
  get(communicationId: string): Observable<any> {
    return this.apiService.apiCall('GET', `communications/${communicationId}`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Delete permanently a communication
   *
   * @since 1.0.0
   *
   * @param communicationId ID of the communication
   */
  delete(communicationId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `communications/${communicationId}`);
  }

  /**
   * Emit a new value (always true) in the "createdCommunication$" behaviourSubject
   * This is used to let communication list knows when a communication has been
   * created from outside
   *
   * @since 1.0.0
   */
  onCommunicationCreated(): void {
    this.createdCommunication$.next(true);
  }
}
